import { type FormEvent, useState } from 'react';

import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

const LIMITE_DO_EDITAL = 5 * 1024 * 1024;

interface EnvioDoEditalProps {
  aberto: boolean;
  aoFechar: () => void;
  exigeMotivo: boolean;
  enviando: boolean;
  erro: string | null;
  aoEnviar: (arquivo: File, motivo: string | undefined) => void;
}

/** Envio (ou troca) do PDF do edital. Em ciclo aberto, com motivo. */
export function EnvioDoEdital({
  aberto,
  aoFechar,
  exigeMotivo,
  enviando,
  erro,
  aoEnviar,
}: EnvioDoEditalProps) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [motivo, setMotivo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    let problema: string | null = null;
    if (!arquivo) problema = 'Escolha o arquivo do edital, em PDF.';
    else if (!/\.pdf$/i.test(arquivo.name) || (arquivo.type && arquivo.type !== 'application/pdf'))
      problema = 'O edital precisa ser um arquivo PDF.';
    else if (arquivo.size > LIMITE_DO_EDITAL) problema = 'O edital pode ter no máximo 5 MB.';
    else if (exigeMotivo && motivo.trim().length < 10)
      problema = 'Escreva o motivo da troca do edital, com ao menos 10 caracteres.';
    setAviso(problema);
    if (problema || !arquivo) return;
    aoEnviar(arquivo, exigeMotivo ? motivo.trim() : undefined);
  }

  const mensagem = aviso ?? erro;
  return (
    <ResponsiveModal
      open={aberto}
      onOpenChange={(v) => !v && aoFechar()}
      title="Arquivo do edital"
      description="PDF de até 5 MB. O arquivo fica disponível ao público quando o ciclo abre."
      desktopMaxWidth="max-w-lg"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
            Voltar
          </Button>
          <Button type="submit" form="formulario-edital" disabled={enviando}>
            {enviando ? 'Enviando…' : 'Enviar edital'}
          </Button>
        </div>
      }
    >
      <form id="formulario-edital" noValidate onSubmit={enviar} className="space-y-4">
        {mensagem && (
          <Alert variant="destructive">
            <AlertDescription>{mensagem}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="edital-arquivo">Arquivo do edital (PDF)</Label>
          <Input
            id="edital-arquivo"
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
          />
        </div>
        {exigeMotivo && (
          <div className="space-y-1.5">
            <Label htmlFor="edital-motivo">Motivo da troca</Label>
            <Textarea
              id="edital-motivo"
              rows={3}
              maxLength={1000}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </div>
        )}
      </form>
    </ResponsiveModal>
  );
}

interface CancelamentoProps {
  aberto: boolean;
  aoFechar: () => void;
  enviando: boolean;
  erro: string | null;
  aoConfirmar: (motivo: string) => void;
}

/** Cancelar ciclo exige motivo, que vai para a trilha. */
export function CancelamentoDoCiclo({
  aberto,
  aoFechar,
  enviando,
  erro,
  aoConfirmar,
}: CancelamentoProps) {
  const [motivo, setMotivo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  function confirmar(evento: FormEvent) {
    evento.preventDefault();
    if (motivo.trim().length < 10) {
      setAviso('Escreva o motivo do cancelamento, com ao menos 10 caracteres.');
      return;
    }
    setAviso(null);
    aoConfirmar(motivo.trim());
  }

  const mensagem = aviso ?? erro;
  return (
    <ResponsiveModal
      open={aberto}
      onOpenChange={(v) => !v && aoFechar()}
      title="Cancelar ciclo"
      description="O ciclo cancelado não recebe inscrições e não volta a abrir. O motivo fica na trilha."
      desktopMaxWidth="max-w-lg"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={aoFechar} disabled={enviando}>
            Voltar
          </Button>
          <Button
            type="submit"
            form="formulario-cancelamento"
            variant="destructive"
            disabled={enviando}
          >
            {enviando ? 'Cancelando…' : 'Cancelar ciclo'}
          </Button>
        </div>
      }
    >
      <form id="formulario-cancelamento" noValidate onSubmit={confirmar} className="space-y-4">
        {mensagem && (
          <Alert variant="destructive">
            <AlertDescription>{mensagem}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="cancelamento-motivo">Motivo do cancelamento</Label>
          <Textarea
            id="cancelamento-motivo"
            rows={3}
            maxLength={1000}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      </form>
    </ResponsiveModal>
  );
}
